import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Popover, TextField } from '@mui/material';

interface RedTimePickerProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  error?: boolean;
}

const HOURS = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0'));
const MINUTES = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));
const PERIODS: ('AM' | 'PM')[] = ['AM', 'PM'];

function parseTimeParts(val: string): {
  hour: string;
  minute: string;
  period: 'AM' | 'PM';
  hasValue: boolean;
} {
  if (!val || !val.trim()) {
    return { hour: '11', minute: '00', period: 'AM', hasValue: false };
  }
  const m12 = val.match(/^(\d{1,2}):(\d{2})\s*(am|pm)$/i);
  if (m12) {
    const h = parseInt(m12[1], 10);
    const m = m12[2];
    const p = m12[3].toUpperCase() as 'AM' | 'PM';
    return { hour: String(h).padStart(2, '0'), minute: m, period: p, hasValue: true };
  }
  const m24 = val.match(/^(\d{1,2}):(\d{2})$/);
  if (m24) {
    let h = parseInt(m24[1], 10);
    const m = m24[2];
    const p = h >= 12 ? 'PM' : 'AM';
    h = h % 12 || 12;
    return { hour: String(h).padStart(2, '0'), minute: m, period: p, hasValue: true };
  }
  return { hour: '11', minute: '00', period: 'AM', hasValue: false };
}

export const RedTimePicker: React.FC<RedTimePickerProps> = ({
  value,
  onChange,
  placeholder = '--:-- --',
  error = false,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const hourListRef = useRef<HTMLDivElement>(null);
  const minuteListRef = useRef<HTMLDivElement>(null);

  const parts = parseTimeParts(value);
  const currentHour = parts.hour;
  const currentMinute = parts.minute;
  const currentPeriod = parts.period;

  const open = Boolean(anchorEl);

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
    if (!parts.hasValue) {
      onChange(`${currentHour}:${currentMinute} ${currentPeriod}`);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const updateTime = (h: string, m: string, p: 'AM' | 'PM') => {
    onChange(`${h}:${m} ${p}`);
  };

  useEffect(() => {
    if (open) {
      setTimeout(() => {
        if (hourListRef.current) {
          const hourIdx = HOURS.indexOf(currentHour);
          if (hourIdx !== -1) {
            hourListRef.current.scrollTop = Math.max(0, hourIdx * 34 - 60);
          }
        }
        if (minuteListRef.current) {
          const minIdx = parseInt(currentMinute, 10);
          if (!isNaN(minIdx)) {
            minuteListRef.current.scrollTop = Math.max(0, minIdx * 34 - 60);
          }
        }
      }, 50);
    }
  }, [open, currentHour, currentMinute]);

  const displayString = parts.hasValue ? `${parts.hour}:${parts.minute} ${parts.period}` : '';

  return (
    <>
      <TextField
        fullWidth
        size="small"
        placeholder={placeholder}
        value={displayString}
        onClick={handleOpen}
        error={error}
        InputProps={{
          readOnly: true,
          sx: {
            cursor: 'pointer',
            fontSize: '14px',
            background: '#fff',
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#EE3750',
              borderWidth: 2,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#EE3750',
            },
          },
        }}
      />

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            mt: 0.5,
            p: 1.5,
            borderRadius: '10px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
            border: '1px solid #F8D3D8',
            background: '#fff',
          },
        }}
      >
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'stretch' }}>
          {/* Hours Column */}
          <Box
            ref={hourListRef}
            sx={{
              maxHeight: 180,
              width: 52,
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              pr: 0.5,
              '&::-webkit-scrollbar': { width: 4 },
              '&::-webkit-scrollbar-thumb': { background: '#f0c0c6', borderRadius: 4 },
            }}
          >
            {HOURS.map((h) => {
              const selected = h === currentHour;
              return (
                <Box
                  key={h}
                  onClick={() => updateTime(h, currentMinute, currentPeriod)}
                  sx={{
                    py: 0.75,
                    px: 1,
                    textAlign: 'center',
                    cursor: 'pointer',
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontWeight: selected ? 700 : 500,
                    background: selected ? '#EE3750' : 'transparent',
                    color: selected ? '#fff' : '#1b1b1f',
                    transition: 'all 0.15s ease',
                    '&:hover': {
                      background: selected ? '#EE3750' : '#FDF0F1',
                      color: selected ? '#fff' : '#EE3750',
                    },
                  }}
                >
                  {h}
                </Box>
              );
            })}
          </Box>

          {/* Divider */}
          <Box sx={{ width: '1px', background: '#f0f0f0' }} />

          {/* Minutes Column */}
          <Box
            ref={minuteListRef}
            sx={{
              maxHeight: 180,
              width: 52,
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              pr: 0.5,
              '&::-webkit-scrollbar': { width: 4 },
              '&::-webkit-scrollbar-thumb': { background: '#f0c0c6', borderRadius: 4 },
            }}
          >
            {MINUTES.map((m) => {
              const selected = m === currentMinute;
              return (
                <Box
                  key={m}
                  onClick={() => updateTime(currentHour, m, currentPeriod)}
                  sx={{
                    py: 0.75,
                    px: 1,
                    textAlign: 'center',
                    cursor: 'pointer',
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontWeight: selected ? 700 : 500,
                    background: selected ? '#EE3750' : 'transparent',
                    color: selected ? '#fff' : '#1b1b1f',
                    transition: 'all 0.15s ease',
                    '&:hover': {
                      background: selected ? '#EE3750' : '#FDF0F1',
                      color: selected ? '#fff' : '#EE3750',
                    },
                  }}
                >
                  {m}
                </Box>
              );
            })}
          </Box>

          {/* Divider */}
          <Box sx={{ width: '1px', background: '#f0f0f0' }} />

          {/* AM / PM Column */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              width: 52,
            }}
          >
            {PERIODS.map((p) => {
              const selected = p === currentPeriod;
              return (
                <Box
                  key={p}
                  onClick={() => updateTime(currentHour, currentMinute, p)}
                  sx={{
                    py: 1,
                    px: 1,
                    textAlign: 'center',
                    cursor: 'pointer',
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontWeight: selected ? 700 : 600,
                    background: selected ? '#EE3750' : 'transparent',
                    color: selected ? '#fff' : '#1b1b1f',
                    transition: 'all 0.15s ease',
                    '&:hover': {
                      background: selected ? '#EE3750' : '#FDF0F1',
                      color: selected ? '#fff' : '#EE3750',
                    },
                  }}
                >
                  {p}
                </Box>
              );
            })}
          </Box>
        </Box>

        <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #f0f0f0', textAlign: 'right' }}>
          <Button
            size="small"
            onClick={handleClose}
            sx={{
              background: '#EE3750',
              color: '#fff',
              fontSize: '11px',
              fontWeight: 700,
              textTransform: 'none',
              borderRadius: '6px',
              py: 0.25,
              px: 1.5,
              '&:hover': { background: '#d42d44' },
            }}
          >
            Done
          </Button>
        </Box>
      </Popover>
    </>
  );
};

export default RedTimePicker;
